/**
 * UK Responsible Person (UKRP) landing page — QbD Group
 * Rebuilt from Figma "V2 desktop 1440" / "V2 mobile 390" (uploaded UKRPPage.tsx).
 *
 * Adapted to the site shell: uses the shared Navbar (Regulatory Affairs logo)
 * and Footer instead of the Figma header/footer. Open Sans comes from the
 * global theme (font-sans).
 *
 * Before publishing:
 *  1. Replace every <Todo> (yellow) with confirmed content from QbD, then delete the Todo component usages.
 */

import { useEffect, useState, type ReactNode } from "react";
import { ArrowRight, Check, ChevronDown } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { HubSpotInlineForm } from "@/components/HubSpotInlineForm";
import { DIVISION_LOGOS } from "@/lib/divisionLogos";
import heroPhoto from "@/assets/ukrp/ukrp-hero.jpg.asset.json";
import ctaPhoto from "@/assets/contact-banner.png.asset.json";
import ukrpaLogo from "@/assets/ukrp/ukrpa-logo.jpg.asset.json";

/* ---------------------------------------------------------------------------
   Design tokens (from qbdgroup.com)
   navy #002857 · accent #64A8FC · green #238472 · body #4C5B72 · grey bg #F5F7FA · border #E7EAEF
--------------------------------------------------------------------------- */

const LINKS = {
  quote: "#quote",
  euRep: "/en/services/regulatory-affairs/authorized-representative-eu-rep",
  chRep: "/en/services/regulatory-affairs/swiss-authorized-representative",
  regulatoryAffairs: "/en/services/regulatory-affairs",
  technicalDocumentation: "/en/services/regulatory-affairs/technical-documentation",
  pms: "/en/services/regulatory-affairs/post-market-surveillance",
  materiovigilance: "/en/services/regulatory-affairs/materiovigilance",
  certifications: "/en/certifications",
  contact: "/en/contact",
  ukrpaMembers: "https://ukrp-association.org/members/",
};

/* ---------------------------------------------------------------------------
   Content
--------------------------------------------------------------------------- */

const responsibilities = [
  { title: "MHRA registration", text: "We register you and your devices in DORS before they are placed on the market, and keep the registration current." },
  { title: "Documentation check", text: "We verify that the Declaration of Conformity, the technical documentation and the conformity assessment are in place." },
  { title: "Documents available to the MHRA", text: "We keep a copy of your technical documentation and certificates available for inspection for the required retention period." },
  { title: "Contact point for the MHRA", text: "We answer requests for information, samples or access to devices, and cooperate with the MHRA on corrective actions." },
  { title: "Complaints and incidents", text: "Any complaint or incident report we receive from healthcare professionals, patients or users is forwarded to you immediately. We support your reporting to the MHRA." },
  { title: "Labelling", text: "Where the UKCA mark is applied, our name and address appear on the label, the outer packaging or the IFU. We confirm what applies to your marking route." },
];

const onboardingSteps = [
  { title: "Mandate and setup", text: "We sign the UKRP agreement, collect your manufacturer details and define the device scope: product families, classes, UKCA or CE route." },
  { title: "Compliance check", text: "We review the Declaration of Conformity, technical documentation, labelling and your PMS set-up against the UK Medical Devices Regulations 2002." },
  { title: "MHRA registration", text: "We register you and your devices in DORS and send you the registration confirmation. From here your devices can be placed on the market." },
  { title: "Ongoing representation", text: "We act as your contact point with the MHRA, forward complaints and incidents, keep your documents available and flag regulatory changes that affect you." },
];

const switchingSteps = [
  { title: "New mandate with QbD", text: "We sign the UKRP agreement and take over your documentation while your current mandate is still in place." },
  { title: "Registration transfer in DORS", text: "We register your devices under QbD as UKRP and the previous UKRP ends its registration." },
  { title: "Labelling update where needed", text: "Where the UKCA mark is applied, our name and address replace the previous UKRP on labels and IFU, with a transition plan for labelled stock." },
];

const representatives = [
  { name: "UK Responsible Person", market: "Great Britain (England, Scotland, Wales)", need: "You are established outside the UK and sell into Great Britain", regulator: "MHRA", legal: "UK Medical Devices Regulations 2002 (as amended)", labelling: "UKRP name and address on label, packaging or IFU where the UKCA mark is applied", registration: "MHRA DORS" },
  { name: "EU Authorised Representative", market: "EU member states and Northern Ireland", need: "You are established outside the EU and sell into the EU or Northern Ireland", regulator: "National competent authorities", legal: "EU MDR 2017/745 and IVDR 2017/746", labelling: "EU AR name and address on the label", registration: "EUDAMED" },
  { name: "Swiss Authorised Representative", market: "Switzerland", need: "You are established outside Switzerland and sell into Switzerland", regulator: "Swissmedic", legal: "Swiss MedDO and IvDO", labelling: "CH-REP name and address on the label", registration: "Swissdamed" },
];

const faqs = [
  { q: "Who needs a UK Responsible Person?", a: "Any manufacturer established outside the UK that places medical devices or IVDs on the Great Britain market (England, Scotland, Wales). All classes count, including Class I, custom-made devices and software. UK importers and distributors do not need one: the obligation sits with the manufacturer. An EU Authorised Representative does not cover Great Britain, so EU manufacturers need a UKRP as well." },
  { q: "Can our UK importer or distributor act as our UKRP?", a: "Legally yes, if you appoint them in writing. In practice it ties your mandate, your MHRA registration and your technical file to one commercial relationship. If that relationship ends, your representation ends with it. An independent UKRP keeps the two apart." },
  { q: "Do we still need a UKRP if our device is CE-marked?", a: "Yes. CE-marked devices are accepted in Great Britain for a transition period, until 30.06.2028 or 30.06.2030 depending on the device type and the validity of its certificate. The UKRP obligation applies from the first device you place on the market, whatever the marking route." },
  { q: "Does a UKRP cover Northern Ireland?", a: "No. Northern Ireland follows EU MDR and IVDR. Manufacturers outside the EU need an EU Authorised Representative there. QbD Group provides that through its EU-REP service." },
  { q: "What do we need to provide?", a: "The Declaration of Conformity, technical documentation, labelling and IFU, manufacturer contact details and your post-market surveillance documentation. We send a checklist with the mandate." },
  { q: "Does the UKRP name have to appear on the label?", a: "Where the UKCA mark is applied, yes: our name and address must appear on the label, the outer packaging or the IFU. For devices placed on the Great Britain market under the CE transitional arrangements this does not apply. There is no official UKRP symbol." },
  { q: "Can one UKRP represent all our devices?", a: "Yes. One mandate can cover your full portfolio of medical devices, IVDs and software. The agreement lists the device families and classes in scope and we extend it when you add products." },
  { q: "Is a UKRP the same as an EU Authorised Representative?", a: "The duties are similar, the markets are not. The UKRP acts in Great Britain under the UK Medical Devices Regulations 2002 and answers to the MHRA. The EU-REP acts in the EU under MDR and IVDR. Neither replaces the other." },
  { q: "What does a UKRP cost, and what about MHRA fees?", a: "Our fee depends on the size of your portfolio and the services you combine with it; we quote a fixed annual amount. MHRA registration fees are separate: since 01.04.2026 the MHRA charges manufacturers an annual fee per device category, paid through DORS.", todo: "QbD: ok to mention the MHRA fee here? The 2026/27 amount is 300£ per category and changes every April." },
  { q: "What happens if we sell in Great Britain without a UKRP?", a: "Your devices cannot be registered with the MHRA, and unregistered devices cannot legally be placed on the Great Britain market." },
];

const relatedServices = [
  { title: "MHRA device registration", text: "Standalone DORS registration and updates for manufacturers that already have a UKRP.", href: `${LINKS.regulatoryAffairs}?ref=ukrp-mhra-registration` },
  { title: "UKCA marking support", text: "Gap assessment, UK approved body selection and the path from CE to UKCA before the transition ends.", href: `${LINKS.regulatoryAffairs}?ref=ukrp-ukca` },
  { title: "UK labelling and IFU review", text: "Label and IFU check for UK requirements, including UKRP details where the UKCA mark is applied.", href: `${LINKS.regulatoryAffairs}?ref=ukrp-labelling` },
  { title: "Technical documentation review", text: "Review of your technical file and Declaration of Conformity against the UK Medical Devices Regulations 2002.", href: LINKS.technicalDocumentation },
  { title: "Post-market surveillance and vigilance", text: "PMS plans, trend reporting and MHRA incident reporting, by the team that handles your UKRP vigilance duties.", href: LINKS.pms },
  { title: "EU and Swiss representation", text: "EU Authorised Representative and CH-REP services from the same group as your UKRP.", href: LINKS.euRep },
];

/* ---------------------------------------------------------------------------
   Small building blocks
--------------------------------------------------------------------------- */

/** Yellow placeholder for content QbD still has to confirm. Remove before publishing. */
function Todo({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <span data-todo className={`inline-block rounded border border-[#E6C94A] bg-[#FFF4C2] px-2 py-0.5 text-[#5A4300] ${className}`}>
      {children}
    </span>
  );
}

function Eyebrow({ children, light = false }: { children: ReactNode; light?: boolean }) {
  return <p className={`text-xs font-semibold uppercase tracking-[0.12em] ${light ? "text-[#64A8FC]" : "text-[#238472]"}`}>{children}</p>;
}

function SectionHeader({ eyebrow, title, intro, dark = false }: { eyebrow?: string; title: string; intro?: ReactNode; dark?: boolean }) {
  return (
    <div className="max-w-3xl space-y-3 lg:space-y-4">
      {eyebrow && <Eyebrow light={dark}>{eyebrow}</Eyebrow>}
      <h2 className={`text-[26px] font-bold leading-[34px] lg:text-4xl lg:leading-[46px] ${dark ? "text-white" : "text-[#002857]"}`}>{title}</h2>
      {intro && <p className={`text-[15px] leading-6 lg:text-lg lg:leading-7 ${dark ? "text-white/85" : "text-[#4C5B72]"}`}>{intro}</p>}
    </div>
  );
}

function Button({ href, children, variant = "primary", className = "" }: { href: string; children: ReactNode; variant?: "primary" | "secondary" | "outlineLight"; className?: string }) {
  const styles = {
    primary: "bg-[#64A8FC] text-[#002857] hover:bg-[#4f98f2]",
    secondary: "bg-[#002857] text-white hover:bg-[#013a7e]",
    outlineLight: "border-[1.5px] border-white/70 text-white hover:bg-white/10",
  }[variant];
  return (
    <a href={href} className={`inline-flex items-center justify-center rounded-full px-7 py-3 text-[15px] font-semibold transition ${styles} ${className}`}>
      {children}
    </a>
  );
}

function CheckIcon({ color = "#238472" }: { color?: string }) {
  return (
    <span className="mt-0.5 inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: color }}>
      <Check className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
    </span>
  );
}

/**
 * Item that is a plain block on desktop and an accordion row on mobile.
 * `defaultOpen` controls the mobile state only; desktop always shows the body.
 */
function MobileAccordionItem({ title, children, badge, defaultOpen = false, className = "" }: { title: ReactNode; children: ReactNode; badge?: ReactNode; defaultOpen?: boolean; className?: string }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={className}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex w-full items-start gap-3 text-left lg:pointer-events-none">
        {badge}
        <span className="flex-1 text-[15px] font-bold leading-6 text-[#002857] lg:text-[17px]">{title}</span>
        <ChevronDown className={`mt-0.5 h-5 w-5 shrink-0 text-[#002857] transition lg:hidden ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`${open ? "block" : "hidden"} lg:block`}>{children}</div>
    </div>
  );
}

function Accordion({ q, a, todo, defaultOpen = false }: { q: string; a: string; todo?: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-[#E7EAEF] bg-white px-5 py-4 lg:px-6 lg:py-5">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex w-full items-start justify-between gap-3 text-left">
        <span className="text-[15px] font-bold leading-6 text-[#002857] lg:text-base">{q}</span>
        <ChevronDown className={`mt-0.5 h-5 w-5 shrink-0 text-[#002857] transition ${open ? "rotate-180" : ""}`} />
      </button>
      {/* Answer stays in the DOM (hidden) so search engines and the FAQPage schema can read it. */}
      <div className={`${open ? "mt-2 block" : "hidden"} text-sm leading-[22px] text-[#4C5B72]`}>
        {a} {todo && <Todo className="ml-1 text-xs">[{todo}]</Todo>}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Sections
--------------------------------------------------------------------------- */

function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#002857] text-white">
      <img src={heroPhoto.url} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#002857]/90 to-[#002857]/75 lg:bg-gradient-to-r lg:from-[#002857]/95 lg:via-[#002857]/80 lg:to-[#002857]/55" />
      <div className="relative mx-auto max-w-[1152px] px-5 pb-9 pt-[120px] lg:px-6 lg:pb-[72px] lg:pt-[180px]">
        <p className="mb-4 text-[11px] uppercase tracking-[0.08em] text-white/65 lg:text-xs">
          <span className="hidden lg:inline">Home / Services / </span>Regulatory Affairs / UK Responsible Person
        </p>
        <h1 className="max-w-[820px] text-[32px] font-bold leading-10 lg:text-[50px] lg:leading-[60px]">UK Responsible Person (UKRP) for Medical Devices &amp; IVDs</h1>
        <p className="mt-4 max-w-[720px] text-[15px] leading-6 text-white/90 lg:mt-5 lg:text-[19px] lg:leading-[30px]">
          For manufacturers outside the UK placing medical devices or IVDs on the Great Britain market. QbD acts as your UK Responsible Person from our London entity: MHRA registration, documentation checks and the ongoing legal duties.
        </p>
        <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
          <Button href={LINKS.quote} className="w-full lg:w-auto">Request a UKRP quote</Button>
          <span className="text-sm text-white/75">
            Reply within <Todo>[QbD to confirm]</Todo> working days
          </span>
        </div>
        <ul className="mt-5 flex flex-wrap gap-2 lg:mt-6">
          {["UKRPA member", "ISO 13485:2016, certified by LRQA", "UK legal entity in London", "Part of QbD Group"].map((t) => (
            <li key={t} className="rounded-full border border-white/30 bg-white/10 px-3 py-1.5 text-xs font-semibold lg:text-[13px]">{t}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function WhyQbd() {
  const points = [
    {
      title: "UK, EU and Swiss representation through one group",
      text: "QbD Group provides UKRP, EU Authorised Representative and Swiss Authorised Representative services, and has offices across Europe and the Americas. Adding a market later runs through the same team.",
      todo: "Sales to confirm the coordination and contact arrangements. Add any combined contract or documentation benefit once verified. Which offices or regions can we name? \"Europe and the Americas\" is what the website says today.",
    },
    {
      title: "Regulatory support beyond representation",
      text: "When you need support beyond your UKRP mandate, QbD Group also has specialists in regulatory affairs, quality assurance, clinical work and post-market surveillance.",
    },
    {
      title: "Experience in medical devices and IVDs",
      todo: "Sales to add a specific, verified statement about the team’s representation experience.",
    },
  ];

  return (
    <section className="bg-white py-14 lg:py-[88px]">
      <div className="mx-auto max-w-[1152px] px-5 lg:px-6">
        <SectionHeader
          eyebrow="Why QbD Group"
          title="Your UKRP, independent of your distributor"
          intro="With QbD as your UK Responsible Person, you can change importers or distributors while keeping your UKRP mandate and MHRA registrations with the same regulatory partner."
        />
        <div className="mt-8 grid gap-10 lg:mt-12 lg:grid-cols-[560px_528px] lg:justify-between lg:gap-16">
          <ul className="space-y-5 lg:space-y-6">
            {points.map((p) => (
              <li key={p.title} className="flex gap-3">
                <CheckIcon />
                <div className="space-y-1.5">
                  <h3 className="text-base font-bold leading-6 text-[#002857] lg:text-[17px]">{p.title}</h3>
                  {p.text && <p className="text-sm leading-[22px] text-[#4C5B72] lg:text-[15px] lg:leading-6">{p.text}</p>}
                  {p.todo && <Todo className="text-sm leading-[22px]">[{p.todo}]</Todo>}
                </div>
              </li>
            ))}
          </ul>

          {/* Proof column: desktop only (removed on mobile to keep the page short) */}
          <div className="hidden space-y-5 lg:block">
            <div className="flex items-center gap-5 rounded-2xl bg-[#F5F7FA] p-6">
              <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full border border-dashed border-[#E6C94A] bg-[#FFF4C2] text-xs text-[#5A4300]">Photo</div>
              <div className="space-y-1">
                <Todo className="text-lg font-bold">[Name], UKRP specialist</Todo>
                <br />
                <Todo className="text-sm">[Role], QbD, London</Todo>
                <br />
                <Todo className="text-sm">“[One line in their own words about how they work with manufacturers.]”</Todo>
              </div>
            </div>

            <div className="rounded-2xl bg-[#002857] p-7 text-white">
              <Eyebrow light>Client example</Eyebrow>
              <p className="mt-2"><Todo className="text-xl font-bold">[Device type] manufacturer, [country]</Todo></p>
              <dl className="mt-4 space-y-3 text-sm leading-[22px]">
                {[
                  ["Starting point", "[CE-marked portfolio, no UK entity, UK distributor asking for a UKRP before the next order.]"],
                  ["Why QbD", "[Wanted the UKRP separate from the distributor and one partner for EU and UK representation.]"],
                  ["Today", "[Devices registered with the MHRA with QbD as UKRP; EU-REP mandate added later.]"],
                ].map(([k, v]) => (
                  <div key={k} className="grid grid-cols-[110px_1fr] gap-4">
                    <dt className="text-xs text-white/70">{k}</dt>
                    <dd><Todo className="text-sm">{v}</Todo></dd>
                  </div>
                ))}
              </dl>
            </div>
            <Todo className="text-xs font-semibold leading-[17px]">
              [QbD: can you give me one client we may use here, approved by them? Starting point, why they chose you, where they are now. No numbers needed. And who is the UKRP specialist we name above, with photo?]
            </Todo>

            <div className="grid grid-cols-2 gap-3">
              <a href={LINKS.certifications} className="flex items-center gap-3 rounded-[10px] border border-[#E7EAEF] bg-white p-3 hover:border-[#64A8FC]">
                <span className="flex h-12 w-[84px] shrink-0 items-center justify-center rounded-lg border border-dashed border-[#E6C94A] bg-[#FFF4C2] p-2 text-xs font-semibold text-[#5A4300]">
                  LRQA logo
                </span>
                <span>
                  <span className="block text-sm font-bold text-[#002857]">ISO 13485:2016</span>
                  <span className="block text-xs leading-[17px] text-[#4C5B72]">Certified by LRQA, cert. 10791765, valid to 13.09.2027. View certificate →</span>
                </span>
              </a>
              <a href={LINKS.ukrpaMembers} className="flex items-center gap-3 rounded-[10px] border border-[#E7EAEF] bg-white p-3 hover:border-[#64A8FC]">
                <span className="flex h-12 w-[84px] shrink-0 items-center justify-center rounded-lg border border-[#E7EAEF] bg-white p-2">
                  <img src={ukrpaLogo.url} alt="UKRPA" className="h-6 w-auto object-contain" />
                </span>
                <span>
                  <span className="block text-sm font-bold text-[#002857]">UKRPA member</span>
                  <span className="block text-xs leading-[17px] text-[#4C5B72]">UK Responsible Person Association. View membership →</span>
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Responsibilities() {
  return (
    <section className="bg-[#F5F7FA] py-14 lg:py-[88px]">
      <div className="mx-auto max-w-[1152px] px-5 lg:px-6">
        <SectionHeader
          eyebrow="What the service includes"
          title="The responsibilities we take on as your UKRP"
          intro="These are the statutory duties of a UK Responsible Person under the UK Medical Devices Regulations 2002. We carry all of them for every device in your mandate."
        />
        <ol className="mt-6 divide-y divide-[#E7EAEF] lg:mt-10 lg:grid lg:grid-cols-3 lg:gap-5 lg:divide-y-0">
          {responsibilities.map((r, i) => (
            <li key={r.title} className="py-3 lg:rounded-[14px] lg:border lg:border-[#E7EAEF] lg:bg-white lg:p-6">
              <MobileAccordionItem
                defaultOpen={i === 0}
                title={r.title}
                badge={<span className="w-6 pt-0.5 text-xs font-semibold tracking-[0.1em] text-[#238472] lg:hidden">{String(i + 1).padStart(2, "0")}</span>}
              >
                <p className="hidden text-xs font-semibold tracking-[0.1em] text-[#238472] lg:mb-2 lg:block">{String(i + 1).padStart(2, "0")}</p>
                <p className="mt-1 pl-9 text-[13px] leading-5 text-[#4C5B72] lg:mt-2 lg:pl-0 lg:text-[15px] lg:leading-6">{r.text}</p>
              </MobileAccordionItem>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Onboarding() {
  return (
    <section className="bg-white py-14 lg:py-[88px]">
      <div className="mx-auto max-w-[1152px] px-5 lg:px-6">
        <SectionHeader eyebrow="How onboarding works" title="From mandate to market in four steps" intro="One sequence, one named contact. We tell you up front what we need from you at each step." />
        <ol className="mt-6 space-y-4 lg:mt-10 lg:grid lg:grid-cols-4 lg:gap-0 lg:space-y-0">
          {onboardingSteps.map((s, i) => (
            <li key={s.title} className="lg:pr-7">
              <div className="hidden items-center gap-3 lg:mb-3 lg:flex">
                <span className={`flex h-9 w-9 items-center justify-center rounded-full text-[15px] font-bold text-white ${i === 3 ? "bg-[#238472]" : "bg-[#002857]"}`}>{i + 1}</span>
                <span className="h-0.5 flex-1 bg-[#E7EAEF]" />
              </div>
              <MobileAccordionItem
                defaultOpen={i === 0}
                title={s.title}
                badge={<span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white lg:hidden ${i === 3 ? "bg-[#238472]" : "bg-[#002857]"}`}>{i + 1}</span>}
              >
                <p className="mt-1 pl-11 text-sm leading-[22px] text-[#4C5B72] lg:mt-2 lg:pl-0">{s.text}</p>
              </MobileAccordionItem>
            </li>
          ))}
        </ol>
        <div className="mt-8 flex flex-col gap-3 lg:mt-10 lg:flex-row lg:items-center lg:gap-4">
          <Button href={LINKS.quote} className="w-full lg:w-auto">Request a UKRP quote</Button>
          <p className="text-[15px] text-[#4C5B72]">Send us your device types and target markets and we come back with a scope and a quote.</p>
        </div>
        <Todo className="mt-4 text-xs font-semibold leading-[17px]">[QbD: how many working days do you need for steps 1 to 3? I only show timings on the page once you confirm them, so for now there are none.]</Todo>
      </div>
    </section>
  );
}

function Switching() {
  return (
    <section className="bg-[#F5F7FA] py-14 lg:py-16">
      <div className="mx-auto max-w-[1152px] px-5 lg:px-6">
        <div className="grid gap-8 lg:grid-cols-[480px_1fr] lg:gap-16">
          <div>
            <Eyebrow>Already on the market</Eyebrow>
            <h2 className="mt-3 text-[26px] font-bold leading-[34px] text-[#002857] lg:text-[28px] lg:leading-9">Switching your UK Responsible Person</h2>
            <p className="mt-3 text-[15px] leading-6 text-[#4C5B72] lg:text-base lg:leading-[26px]">Unhappy with response times, or your distributor holds your mandate?</p>
            <Button href={`${LINKS.quote}?intent=switch`} variant="secondary" className="mt-5 hidden lg:inline-flex">Request a switch quote</Button>
          </div>
          <div>
            <ol className="space-y-4">
              {switchingSteps.map((s, i) => (
                <li key={s.title}>
                  <MobileAccordionItem
                    defaultOpen={i === 0}
                    title={s.title}
                    badge={<span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-[#002857] text-[13px] font-bold text-white">{i + 1}</span>}
                  >
                    <p className="mt-1 pl-[42px] text-sm leading-[22px] text-[#4C5B72]">{s.text}</p>
                  </MobileAccordionItem>
                </li>
              ))}
            </ol>
            <p className="mt-4 text-[13px] leading-5 text-[#4C5B72]">
              We plan the handover so the new mandate is in place before the previous one ends. <Todo className="text-xs">[QbD: please confirm this sequence and whether the MHRA charges a statutory fee for a UKRP change.]</Todo>
            </p>
            <Button href={`${LINKS.quote}?intent=switch`} variant="secondary" className="mt-5 w-full lg:hidden">Request a switch quote</Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Comparison() {
  const [full, setFull] = useState(false);
  const [openCard, setOpenCard] = useState(0);
  const rows: { label: string; key: keyof (typeof representatives)[number]; full?: boolean }[] = [
    { label: "Market covered", key: "market" },
    { label: "You need one if", key: "need" },
    { label: "Regulator", key: "regulator" },
    { label: "Legal basis", key: "legal", full: true },
    { label: "Name on the label", key: "labelling", full: true },
    { label: "Registration system", key: "registration", full: true },
  ];

  return (
    <section className="bg-white py-14 lg:py-[88px]">
      <div className="mx-auto max-w-[1152px] px-5 lg:px-6">
        <SectionHeader eyebrow="UK, EU or Switzerland" title="UKRP, EU-REP or CH-REP: which one do you need?" intro="Each market has its own legal representative and none of them covers another market. QbD Group provides all three." />

        {/* Desktop table */}
        <div className="mt-10 hidden overflow-hidden rounded-[14px] border border-[#E7EAEF] lg:block">
          <table className="w-full text-sm leading-[22px]">
            <thead>
              <tr className="bg-[#002857] text-left text-white">
                <th className="w-[224px] px-5 py-4 font-bold" />
                {representatives.map((r) => <th key={r.name} className="px-5 py-4 font-bold">{r.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.filter((r) => !r.full || full).map((row, i) => (
                <tr key={row.key} className={i % 2 ? "bg-[#F5F7FA]" : "bg-white"}>
                  <th className="px-5 py-4 text-left font-semibold text-[#002857]">{row.label}</th>
                  {representatives.map((r) => <td key={r.name} className="px-5 py-4 text-[#002857]">{r[row.key] as string}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" onClick={() => setFull((f) => !f)} aria-expanded={full} className="flex w-full items-center justify-between border-t border-[#E7EAEF] bg-[#F5F7FA] px-5 py-4 text-left text-sm font-semibold text-[#002857]">
            Full comparison: legal basis, labelling rules and registration systems
            <ChevronDown className={`h-5 w-5 transition ${full ? "rotate-180" : ""}`} />
          </button>
        </div>

        {/* Mobile cards */}
        <div className="mt-6 space-y-3 lg:hidden">
          {representatives.map((r, i) => {
            const open = openCard === i;
            return (
              <div key={r.name} className="overflow-hidden rounded-xl border border-[#E7EAEF]">
                <button type="button" onClick={() => setOpenCard(open ? -1 : i)} aria-expanded={open} className="flex w-full items-center justify-between bg-[#002857] px-4 py-3 text-left text-[15px] font-bold text-white">
                  {r.name}
                  <ChevronDown className={`h-5 w-5 transition ${open ? "rotate-180" : ""}`} />
                </button>
                <dl className={open ? "block" : "hidden"}>
                  {rows.filter((row) => !row.full || full).map((row, j) => (
                    <div key={row.key} className={`px-4 py-2.5 ${j % 2 ? "bg-[#F5F7FA]" : "bg-white"}`}>
                      <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#4C5B72]">{row.label}</dt>
                      <dd className="text-sm leading-[21px] text-[#002857]">{r[row.key] as string}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            );
          })}
          <button type="button" onClick={() => setFull((f) => !f)} aria-expanded={full} className="flex w-full items-center justify-between rounded-xl border border-[#E7EAEF] bg-[#F5F7FA] px-4 py-3.5 text-left text-sm font-semibold text-[#002857]">
            Full comparison: legal basis, labelling, registration systems
            <ChevronDown className={`h-5 w-5 shrink-0 transition ${full ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>
    </section>
  );
}

function Faq() {
  const [showAll, setShowAll] = useState(false);
  const left = faqs.slice(0, 5);
  const right = faqs.slice(5);

  return (
    <section className="bg-[#F5F7FA] py-14 lg:py-[88px]">
      <div className="mx-auto max-w-[1152px] px-5 lg:px-6">
        <SectionHeader eyebrow="FAQ" title="UK Responsible Person: frequently asked questions" />
        <div className="mt-6 grid gap-3 lg:mt-10 lg:grid-cols-2 lg:gap-6">
          <div className="space-y-3">{left.map((f, i) => <Accordion key={f.q} {...f} defaultOpen={i === 0} />)}</div>
          <div className={`space-y-3 ${showAll ? "block" : "hidden lg:block"}`}>{right.map((f) => <Accordion key={f.q} {...f} />)}</div>
        </div>
        {!showAll && (
          <button type="button" onClick={() => setShowAll(true)} className="mt-3 flex w-full items-center justify-between rounded-xl border border-[#E7EAEF] bg-white px-[18px] py-3.5 text-[15px] font-semibold text-[#002857] lg:hidden">
            Show all {faqs.length} questions <ChevronDown className="h-5 w-5" />
          </button>
        )}
      </div>
    </section>
  );
}

function QuoteForm() {
  const [scopeOpen, setScopeOpen] = useState(false);

  const scope = (
    <ul className="space-y-1.5 text-sm leading-[22px] text-white/85 lg:text-[15px] lg:leading-[23px]">
      <li>· <Todo className="text-sm">Included: UKRP representation as an annual fee, and the DORS registration work [QbD to confirm]</Todo></li>
      <li>· <Todo className="text-sm">Not included: MHRA registration fees. Since 01.04.2026 the MHRA charges manufacturers an annual fee per device category, paid through DORS [QbD to confirm]</Todo></li>
      <li>· <Todo className="text-sm">Quoted separately: technical documentation or labelling reviews beyond the compliance check [QbD to confirm scope]</Todo></li>
    </ul>
  );

  return (
    <section id="quote" className="relative overflow-hidden bg-[#002857] py-14 text-white lg:py-[88px]">
      <img src={ctaPhoto.url} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-[#002857]/85" />
      <div className="relative mx-auto grid max-w-[1152px] gap-8 px-5 lg:grid-cols-[560px_528px] lg:justify-between lg:gap-16 lg:px-6">
        <div className="space-y-7">
          <SectionHeader dark eyebrow="Request a UKRP quote" title="Get a quote for your devices" intro="Tell us what you make and where you sell. We come back with a scope and a fixed annual fee." />
          {/* Explanation blocks: on mobile they render below the form via order classes */}
          <div className="hidden space-y-7 lg:block">
            <Block title="What we need first" items={["Device type and, if you know them, the classes", "Where you are established and which markets you sell into", "Whether you already have a UKRP today"]} />
            <Block title="What happens next" items={[<>We reply within <Todo>[QbD to confirm]</Todo> working days</>, <>A short call before quoting: <Todo>[QbD to confirm whether a call is always needed]</Todo></>, "You receive the proposal and the UKRP agreement"]} />
            <div className="space-y-2">
              <p className="text-sm font-bold">What the quote covers</p>
              {scope}
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-white p-5 text-[#002857] lg:rounded-[20px] lg:p-8">
          <p className="text-lg font-bold lg:text-xl">Request your UKRP quote</p>
          {/* Same HubSpot contact form the UKRP service page uses (Regulatory Affairs, portal 7030766) */}
          <div className="qbd-hsform mt-4">
            <HubSpotInlineForm formId="fd440404-5038-4215-ab1f-c34b52e39276" />
          </div>
          <p className="mt-4 text-xs text-[#4C5B72]">We use your details to answer this request only.</p>
        </div>

        {/* Mobile explanation, below the form */}
        <div className="space-y-6 lg:hidden">
          <Block title="What we need first" items={["Device type and, if you know them, the classes", "Where you are established and which markets you sell into", "Whether you already have a UKRP today"]} />
          <Block title="What happens next" items={[<>We reply within <Todo>[QbD to confirm]</Todo> working days</>, <>A short call before quoting: <Todo>[QbD to confirm whether a call is always needed]</Todo></>, "You receive the proposal and the UKRP agreement"]} />
          <button type="button" onClick={() => setScopeOpen((o) => !o)} aria-expanded={scopeOpen} className="flex w-full items-center justify-between rounded-[10px] border border-white/30 bg-white/10 px-3.5 py-3 text-left text-sm font-semibold">
            What the quote covers, and what it does not <ChevronDown className={`h-5 w-5 shrink-0 transition ${scopeOpen ? "rotate-180" : ""}`} />
          </button>
          <div className={scopeOpen ? "block" : "hidden"}>{scope}</div>
        </div>
      </div>
    </section>
  );
}

function Block({ title, items }: { title: string; items: ReactNode[] }) {
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-bold">{title}</p>
      <ul className="space-y-1.5 text-sm leading-[22px] text-white/85 lg:text-[15px] lg:leading-[23px]">
        {items.map((it, i) => <li key={i}>· {it}</li>)}
      </ul>
    </div>
  );
}

function RelatedServices() {
  return (
    <section className="bg-[#F5F7FA] py-10 lg:py-[88px]">
      <div className="mx-auto max-w-[1152px] px-5 lg:px-6">
        {/* Desktop: six cards */}
        <div className="hidden lg:block">
          <SectionHeader eyebrow="Beyond representation" title="Other UK market services from QbD Group" intro="The UKRP mandate is the legal minimum. These services sit in the same group, so one team covers both." />
          <ul className="mt-10 grid grid-cols-3 gap-5">
            {relatedServices.map((s) => (
              <li key={s.title} className="flex flex-col rounded-[14px] border border-[#E7EAEF] bg-white p-6">
                <h3 className="text-[17px] font-bold leading-6 text-[#002857]">{s.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-[22px] text-[#4C5B72]">{s.text}</p>
                <a href={s.href} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#002857] hover:text-[#64A8FC]">Learn more <ArrowRight className="h-4 w-4" /></a>
              </li>
            ))}
          </ul>
        </div>
        {/* Mobile: one link */}
        <a href={LINKS.regulatoryAffairs} className="flex items-center justify-between gap-3 rounded-xl border border-[#E7EAEF] bg-white p-[18px] lg:hidden">
          <span>
            <span className="block text-[15px] font-bold leading-6 text-[#002857]">More UK market services from QbD Group</span>
            <span className="mt-1 block text-[13px] leading-5 text-[#4C5B72]">MHRA registration, UKCA support, labelling and IFU review, technical documentation, PMS and vigilance, EU and Swiss representation.</span>
          </span>
          <ArrowRight className="h-5 w-5 shrink-0 text-[#002857]" />
        </a>
      </div>
    </section>
  );
}

/** Mobile only: fixed CTA bar that appears once the hero has scrolled out of view. */
function StickyCta() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 560);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className={`fixed inset-x-0 bottom-0 z-30 border-t border-[#E7EAEF] bg-white px-5 py-3 transition lg:hidden ${show ? "translate-y-0" : "translate-y-full"}`}>
      <Button href={LINKS.quote} className="w-full">Request a UKRP quote</Button>
    </div>
  );
}

/** FAQPage structured data, built from the same faqs array so it never drifts from the page. */
function FaqSchema() {
  const json = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }} />;
}

/* ---------------------------------------------------------------------------
   Page
--------------------------------------------------------------------------- */

export default function UkrpPage() {
  return (
    <div className="min-h-screen bg-white font-sans text-[#002857] antialiased">
      <FaqSchema />
      <Navbar logoOverride={DIVISION_LOGOS["regulatory-affairs"]} />
      <main>
        <Hero />
        <WhyQbd />
        <Responsibilities />
        <Onboarding />
        <Switching />
        <Comparison />
        <Faq />
        <QuoteForm />
        <RelatedServices />
      </main>
      <Footer />
      <StickyCta />
    </div>
  );
}
